
import { Injectable } from '@angular/core';

import { Observable, of, forkJoin } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { SweepOption } from '../sweep-options/sweep-option';
import { SweepOptionTypes } from '../sweep-options/sweep-option-types';

import { AppMessagesService } from '../app-messages/app-messages.service';
import { SweepOptionsService } from '../sweep-options/sweep-options.service';

@Injectable({
    providedIn: 'root'
})
export class AccountAdditionalInfoService {
    constructor(
        private sweepOptionsService: SweepOptionsService,
        private appMessagesService: AppMessagesService
    ) {}

    getAdditionalSecondaryInfos(accountInfos: any[]): Observable<any[]> {
        if (!accountInfos || accountInfos.length === 0) {
            return of([]);
        }

        const accountAdditionalInfoObservableList = [];

        for (const accountInfo of accountInfos) {
            accountAdditionalInfoObservableList.push(
                this.getAdditionalSecondaryInfo(accountInfo)
            );
        }

        return forkJoin(accountAdditionalInfoObservableList);
    }

    private getAdditionalSecondaryInfo(accountInfo: any): Observable<any> {
        const sweepOption = accountInfo ? accountInfo.sweepOption : null;

        return forkJoin(
            this.sweepOptionsService.getSweepOptionBySymbol(sweepOption),
            this.appMessagesService.getAppMessages()
        ).pipe(
            switchMap((results: any[]) => {
                const [sweepOptionInfo, messageInfo] = results;

                return of({
                    referenceId: accountInfo.referenceId,
                    secondaryContractValues: this.prepareAdditionalInfo(accountInfo, sweepOptionInfo, messageInfo)
                });
            })
        );
    }

    private prepareAdditionalInfo(accountInfo: any, sweepOptionInfo: any, messageInfo: any): any[] {
      let additionalInfos = [];

      additionalInfos = additionalInfos.concat(this.prepareAccountYieldInfo(sweepOptionInfo, messageInfo));
      additionalInfos = additionalInfos.concat(this.prepareAccountPerformanceInfo(accountInfo, sweepOptionInfo, messageInfo));

      return additionalInfos;
    }

    private prepareAccountYieldInfo(sweepOptionInfo: SweepOption, messageInfo: any): any[] {
      if (!sweepOptionInfo) {
        return [];
      }

      return this.prepareAccountYields(sweepOptionInfo, messageInfo);
    }

    private prepareAccountPerformanceInfo(accountInfo: any, sweepOptionInfo: any, messageInfo: any): any[] {
      const interestYTD = accountInfo ? accountInfo.interestYTD : null;

      if (!interestYTD) {
        return [];
      }

      const accountPerformanceContent = this.prepareAccountPerformanceContent(sweepOptionInfo, messageInfo);

      return [
          {
            title: accountPerformanceContent.title,
            titleToolTip: {
                title: accountPerformanceContent.title,
                description: accountPerformanceContent.description
            },
            value: interestYTD ? this.formatAmountValue(interestYTD) : '',
            valueType: 'amount'
          }
        ];
    }

    private prepareAccountYields(sweepOptionInfo: SweepOption, messageInfo: any): any[] {
        const yields = sweepOptionInfo.yield || [];
        const symbol = sweepOptionInfo.symbol;

        return yields.map(rawYield => {
            let yieldType: string;
            let defaultTitle: string;

            if (rawYield.durationUnit === 'day' && rawYield.duration === '365') {
                if (symbol === SweepOptionTypes.NFS_MM_FUND || symbol === SweepOptionTypes.PGIM_MM_FUND) {
                    yieldType = 'return_annual';
                    defaultTitle = '1 Year Return';
                } else {
                    yieldType = 'yield_annual';
                    defaultTitle = 'Annual Percentage Yield';
                }
            } else if (rawYield.durationUnit === 'day' && rawYield.duration === '7') {
                yieldType = 'yield_7_day';
                defaultTitle = '7 Day Yield';
            } else {
                defaultTitle = rawYield.duration + ' ' + rawYield.durationUnit + ' yield';
            }

            try {
                const yieldContent = messageInfo.common.messages[yieldType];

                return {
                    title: yieldContent.title,
                    titleDesc: 'As of ' + rawYield.asOfDate,
                    titleToolTip: {
                        title: yieldContent.title,
                        description: yieldContent.content
                    },
                    value: this.formatAmountValue(rawYield.value),
                    valueType: 'percentage'
                };
            } catch (e) {
                return {
                    title: defaultTitle,
                    titleDesc: 'As of ' + rawYield.asOfDate,
                    titleToolTip: {
                        title: defaultTitle,
                        description: defaultTitle
                    },
                    value: this.formatAmountValue(rawYield.value),
                    valueType: 'percentage'
                };
            }
        });
    }

    private prepareAccountPerformanceContent(sweepOptionInfo: any, messageInfo: any): any {
      try {
        let sweepOption = (sweepOptionInfo && sweepOptionInfo.symbol) || null;
        let accountPerformanceContent: any;

        if (sweepOption) {
          sweepOption = 'interest_' + sweepOption.toLowerCase();

          accountPerformanceContent = messageInfo.common.messages[sweepOption];
        }

        accountPerformanceContent = accountPerformanceContent || messageInfo.common.messages.interest_ytd;

        return {
          title: accountPerformanceContent.title,
          description: accountPerformanceContent.content
        };
      } catch (e) {
        return {
          title: 'Income/Interest Earned YTD',
          description: 'Income/Interest Earned YTD'
        };
      }
    }

    private formatAmountValue(rawValue): string {
      try {
        return parseFloat(rawValue).toFixed(2);
      } catch (e) {
        return rawValue;
      }
    }
}
